import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { Box, Typography } from '@mui/material'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQueries } from '@tanstack/react-query'

import {
  MEETING_TAB_ITEMS,
  MeetingBlock,
} from '@/apps/latest/components/organisms/MeetingBlock'
import {
  RecordingCtaCluster,
  recordingStatusCaption,
} from '@/apps/latest/components/molecules/RecordingCtaCluster'
import { RecordingModeMenu } from '@/apps/latest/components/molecules/RecordingModeMenu'
import { PharmacistMenu } from '@/apps/latest/components/molecules/PharmacistMenu'
import { RecordingSettingsPopover } from '@/apps/latest/components/molecules/RecordingSettingsPopover'
import { Layout } from '@/apps/latest/components/templates/Layout'
import { ActiveRecordingProvider } from '@/apps/latest/shared/contexts/ActiveRecordingContext'
import { useActiveRecordingEmit } from '@/apps/latest/shared/recording-status/useActiveRecordingEmit'
import { usePollEvent } from '@/apps/latest/shared/recording-status/usePollEvent'
import { useRecordingStatus } from '@/apps/latest/shared/recording-status/useRecordingStatus'
import { useSessionPharmacist, usePharmacistsQuery } from '@/features/auth'
import { blocksOwnRecording } from '@/features/remoteGuidance'
import { usePatchYakurekiEventMutation } from '@/features/yakureki'
import {
  DEFAULT_RECORDING_PRESET,
  type RecordingPreset,
} from '@/features/yakureki/recording/streaming/recordingPreset'
import { getYakurekiDetail } from '@/shared/apis/yakureki'
import { useToast } from '@/shared/contexts/ToastContext'
import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { typeScale } from '@/shared/styles/typography'

// まとめて録音には添付資料タブを出さない。資料は 1 人の記録に付くが、ここには誰に付けるかを
// 選ぶ口が無く、出すと 1 人目の患者に黙って付く。資料は各患者の記録の画面 (記録ブロックの添付資料タブ・完了後は
// SOAP の下の資料の節) から足す (hub log/108 決定 12)。
const GROUP_MEETING_TABS = [
  MEETING_TAB_ITEMS.highlight,
  MEETING_TAB_ITEMS.transcript,
  MEETING_TAB_ITEMS.memo,
]

// Phase B まとめて録音: 1 streaming session を 1 共通 MeetingBlock で表示する.
const GroupRecordingPage: FC = () => {
  const [searchParams] = useSearchParams()
  const idsParam = searchParams.get('ids') ?? ''
  const yakurekiIds = useMemo(
    () =>
      idsParam
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
    [idsParam]
  )

  const { pharmacists } = usePharmacistsQuery()
  const pharmacistList = useMemo(
    () => (pharmacists?.pharmacists ?? []).filter((p) => p.is_own),
    [pharmacists]
  )
  const { sessionPharmacistId, setSessionPharmacistId } =
    useSessionPharmacist(pharmacistList)
  const patchEventMutation = usePatchYakurekiEventMutation()
  const { showToast } = useToast()

  // N events を parallel fetch. こちらは患者行の表示に使う init データ (氏名 / 同伴既定値)
  // なので polling しない. 録音 lifecycle を駆動する代表 event だけ下で L1 同期する.
  const eventQueries = useQueries({
    queries: yakurekiIds.map((id) => ({
      queryKey: ['yakureki', id, 'group-init'],
      queryFn: async () => (await getYakurekiDetail(id)).data,
      staleTime: 60_000,
    })),
    combine: (results) => ({
      data: results.map((r) => r.data).filter(Boolean),
      isLoading: results.some((r) => r.isLoading),
    }),
  })
  const events = eventQueries.data
  const isLoading = eventQueries.isLoading

  // 代表 event (= ids[0]) を useRecordingStatus に渡す. 他患者は SOAP 完了後に sidebar 経由で閲覧.
  // L1 (server canonical event) は代表 1 件だけ usePollEvent で追う. init fetch は
  // polling しないため、 これが無いと status 遷移 (recording → processing → completed) を
  // 誰も観測できない (以前は MeetingBlock 内 poll が肩代わりしていた).
  const { data: polledRepresentative } = usePollEvent({
    yakurekiId: yakurekiIds[0] ?? null,
  })
  const representativeEvent =
    polledRepresentative ?? events.find((e) => e?.id === yakurekiIds[0])

  // start 時点の context snapshot.
  const startSnapshotRef = useRef<{
    pharmacistId?: string
    memoItemsMap: Record<string, string[]>
    labelMap: Record<string, string>
    hasCompanionMap: Record<string, boolean>
  }>({
    pharmacistId: undefined,
    memoItemsMap: {},
    labelMap: {},
    hasCompanionMap: {},
  })

  // fallback 経路で N events に並列 PATCH.
  const onFallbackCommit = useCallback(
    async (blob: Blob): Promise<void> => {
      if (yakurekiIds.length === 0) {
        throw new Error('yakurekiIds missing for group fallback commit')
      }
      const file = new File([blob], 'audio.mp4', {
        type: blob.type || 'audio/mp4',
      })
      const snap = startSnapshotRef.current
      await Promise.all(
        yakurekiIds.map((yakurekiId) =>
          patchEventMutation.mutateAsync({
            audioSource: file,
            label: snap.labelMap[yakurekiId] ?? '',
            yakurekiId,
            pharmacistId: snap.pharmacistId,
            memoItems: snap.memoItemsMap[yakurekiId] ?? [],
            // 同伴/代理の別: batch 経路は stream/start を経ないため commit で運ぶ (log/207)。
            hasCompanionOrSubstitute: snap.hasCompanionMap[yakurekiId] ?? false,
          })
        )
      )
    },
    [yakurekiIds, patchEventMutation]
  )

  const navigate = useNavigate()
  const recordingStatus = useRecordingStatus({
    event: representativeEvent ?? undefined,
    onFallbackCommit,
  })
  // method 呼び出しの receiver を deps に含めず callback を安定させるため destructure する.
  const { startRecording } = recordingStatus
  useActiveRecordingEmit(recordingStatus)

  // 患者ごとの同伴/代理人有 override. undefined = back の event.has_companion_or_substitute を採用.
  const [companionOverrideMap, setCompanionOverrideMap] = useState<
    Record<string, boolean>
  >({})
  const hasCompanionMap = useMemo(() => {
    const map: Record<string, boolean> = {}
    for (const ev of events) {
      if (!ev) continue
      map[ev.id] =
        companionOverrideMap[ev.id] ?? ev.has_companion_or_substitute ?? false
    }
    return map
  }, [events, companionOverrideMap])

  const handleToggleCompanion = useCallback(
    (patientId: string, value: boolean) => {
      setCompanionOverrideMap((prev) => ({ ...prev, [patientId]: value }))
    },
    []
  )

  // 録音開始時に選ぶ生成モード (標準/深掘り). session 全 event に適用される.
  const [preset, setPreset] = useState<RecordingPreset>(
    DEFAULT_RECORDING_PRESET
  )

  // 患者行 (init fetch) が揃うまで録音を始めさせない (氏名 / 同伴既定値の snapshot が要る).
  const isReady = !isLoading && events.length === yakurekiIds.length

  const handleStartRecording = useCallback(() => {
    if (yakurekiIds.length === 0) return
    // 遠隔に依頼中 (頼む店) の処方は録音しない (通話の音声と二重に書く)。Sidebar の選択で
    // 外しているが、選んだ後に別の端末で頼んだ・URL を直に開いた、を back の無い front で止める
    if (events.some((ev) => ev && blocksOwnRecording(ev.remote))) {
      showToast(
        '遠隔に依頼中の処方が含まれています。この店では録音できません',
        'error'
      )
      return
    }
    const pharmacistId = sessionPharmacistId || undefined
    const memoItemsMap: Record<string, string[]> = {}
    const labelMap: Record<string, string> = {}
    for (const ev of events) {
      if (!ev) continue
      memoItemsMap[ev.id] = ev.memo_items ?? []
      labelMap[ev.id] = ev.label ?? ''
    }
    startSnapshotRef.current = {
      pharmacistId,
      memoItemsMap,
      labelMap,
      hasCompanionMap,
    }
    startRecording({
      yakurekiIds,
      pharmacistId,
      memoItems: [],
      hasCompanionOrSubstituteMap: Object.fromEntries(
        Object.entries(hasCompanionMap).filter(([, v]) => v)
      ),
      preset,
    })
  }, [
    yakurekiIds,
    events,
    showToast,
    sessionPharmacistId,
    startRecording,
    hasCompanionMap,
    preset,
  ])

  // 録音完了で代表患者の SOAP review 画面に遷移.
  // 低速文字起こし flag は engine (= 本 page の hook instance) に閉じるため、
  // recording-new と同様 history state で遷移先の低速バナー表示に引き継ぐ.
  // finalized は「この録音は finalize ack 済み」の証拠 (ADR-0041): 遷移先の engine は
  // 別 instance で idle なので、 サーバが processing を書く前の 1 poll を generating と
  // 読むにはこの hint が要る.
  useEffect(() => {
    if (
      recordingStatus.engine.state === 'completed' &&
      yakurekiIds.length > 0
    ) {
      navigate(`/soap/${yakurekiIds[0]}`, {
        replace: true,
        state: {
          finalized: true,
          ...(recordingStatus.engine.isSlowTranscription
            ? {
                slowTranscription: true,
                intendedBatch: recordingStatus.engine.isIntendedBatch,
              }
            : {}),
        },
      })
      return
    }
    if (
      recordingStatus.engine.state === 'failed' &&
      !recordingStatus.engine.hasPendingBlob
    ) {
      recordingStatus.engine.reset()
    }
  }, [
    recordingStatus.engine.state,
    recordingStatus.engine.hasPendingBlob,
    recordingStatus.engine,
    navigate,
    yakurekiIds,
  ])

  if (yakurekiIds.length === 0) {
    return (
      <Layout breadcrumb={['まとめて録音']}>
        <Typography
          sx={{
            fontSize: typeScale.body.fontSize,
            color: colorTokens.fg.muted,
            textAlign: 'center',
            py: 6,
          }}
        >
          録音対象の薬歴 ID が指定されていません. URL に <code>?ids=A,B,C</code>{' '}
          を付与してください.
        </Typography>
      </Layout>
    )
  }

  const patientNames = events
    .map((ev) => ev?.label || '(無名)')
    .filter(Boolean)
    .join(' ・ ')
  const caption = recordingStatusCaption(recordingStatus)

  return (
    <Layout breadcrumb={['まとめて録音', `${yakurekiIds.length} 名`]}>
      {/* 見出し行 + 録音の終端操作群. 単体 (PatientInfoBar) と同じ部品・同じ文法 (ADR-0040).
          主張 (誰の録音か) だけがこの page のもので、 操作は cluster が持つ. */}
      <Box
        sx={{
          mb: 3,
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          columnGap: { xs: 1, sm: 2 },
          rowGap: 1,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'baseline',
              flexWrap: 'wrap',
              gap: 0.75,
              mb: 0.5,
            }}
          >
            <Typography
              sx={{
                fontSize: typeScale.h2.fontSize,
                fontWeight: typeScale.h2.fontWeight,
                letterSpacing: typeScale.h2.letterSpacing,
                color: colorTokens.fg.default,
              }}
            >
              {yakurekiIds.length} 名同時録音
            </Typography>
            {/* status caption は患者ヘッダと同じ文言・同じ位置 (見出しの脇). */}
            {caption && (
              <Typography variant="caption" color="text.secondary">
                {caption}
              </Typography>
            )}
          </Box>
          {patientNames && (
            <Typography
              sx={{
                fontSize: typeScale.bodySm.fontSize,
                color: colorTokens.fg.muted,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {patientNames}
            </Typography>
          )}
        </Box>
        {/* 一時停止は出さない (allowPause=false): 復元が単一 event 前提で、 まとめて録音を
            一時停止すると reload / 患者切替で再開も再 start もできなくなる (ADR-0040 決定 3). */}
        <RecordingCtaCluster
          recordingStatus={recordingStatus}
          onStartRecording={handleStartRecording}
          allowPause={false}
          startDisabled={!isReady}
          idleControls={
            <>
              {/* 録音モードは batch 経路では出さない (log/207): legacy batch upload に
                  preset の受け口が無く、選ばせても届かないため. */}
              {recordingStatus.transcriptionRoute !== 'batch' && (
                <RecordingModeMenu
                  value={preset}
                  onChange={setPreset}
                  disabled={!isReady}
                />
              )}
              {pharmacistList.length >= 2 && (
                <PharmacistMenu
                  pharmacists={pharmacistList}
                  value={sessionPharmacistId}
                  onChange={setSessionPharmacistId}
                  disabled={!isReady}
                />
              )}
              {/* 並び順: 録音モード → 担当 → 録音設定 (⫻) → mic. */}
              {isReady && (
                <RecordingSettingsPopover
                  patients={events
                    .filter((e): e is NonNullable<typeof e> => Boolean(e))
                    .map((ev) => ({
                      id: ev.id,
                      name: ev.patient_name || ev.label || '(無名)',
                      hasCompanion: hasCompanionMap[ev.id] ?? false,
                    }))}
                  onToggle={handleToggleCompanion}
                />
              )}
            </>
          }
        />
      </Box>
      <MeetingBlock
        yakurekiId={yakurekiIds[0]}
        recordingStatus={recordingStatus}
        tabs={GROUP_MEETING_TABS}
      />
    </Layout>
  )
}

const LatestGroupRecordingPage: FC = () => (
  <ActiveRecordingProvider>
    <GroupRecordingPage />
  </ActiveRecordingProvider>
)

export default LatestGroupRecordingPage
